import {
  Component,
  computed,
  effect,
  ElementRef,
  Input,
  Optional,
  QueryList,
  Self,
  Signal,
  signal,
  ViewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import {
  BlockButton,
  BlockRadioBox,
  BlockType,
  BlockViaSurvey,
  ButtonColor,
  ButtonPosition,
  ButtonType,
  CheckboxItem,
  RadioItem,
  TOKEN_SURVEY_QUERY_PARAM_KEY,
  ViaSurveyStrength,
} from '@src/app/@shared/models/step.model';
import { AbstractControl, FormArray, FormBuilder, FormControl, NgControl, ValidationErrors } from '@angular/forms';
import { BlockAbstractComponent } from '@app/components/blocks/block.abstract.component';
import {
  ApiUrlProgramData,
  CourseContentService,
  ProgramDataCode,
  ShortCodes,
} from '@app/courseContent/courseContent.service';
import { ViaSurveyService } from '@shared/services/viaSurvey/viaSurvey.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { VIA_SURVEY_QUESTION_COUNT, ViaSurveyQuestion } from '@shared/models/viaSurvey.model';
import { getRandomId, getRandomInt } from '@app/utils/general';

export enum FormControlName {
  viaSurveyQuestions = 'viaSurveyQuestions',
  strengthList = 'strengthList',
}

interface ViaSurveyForm {
  [FormControlName.viaSurveyQuestions]: FormArray<FormControl<BlockRadioBox>>;
  [FormControlName.strengthList]: FormControl<CheckboxItem[]>;
}

export enum ViaSurveySection {
  questions = 'questions',
  results = 'results',
}

export interface AllRadioBoxPerIndex {
  [index: string]: BlockRadioBox;
}

@Component({
  selector: 'pl-block-via-survey',
  templateUrl: './block-viaSurvey.component.html',
})
export class BlockViaSurveyComponent extends BlockAbstractComponent<BlockViaSurvey, ViaSurveyForm> {
  @Input({ required: true }) public versionBeforeProgram: boolean = false;
  @Input({ required: true }) public tokenSurvey: string | null = null; // set when versionBeforeProgram === true

  public section: WritableSignal<ViaSurveySection | null> = signal(null);
  public isLoading: WritableSignal<boolean> = signal(true);
  public numberQuestionDisplayed: WritableSignal<number> = signal(20);
  public pageNumber: WritableSignal<number> = signal(1);
  public totalPages: Signal<number> = computed(() =>
    Math.floor(VIA_SURVEY_QUESTION_COUNT / this.numberQuestionDisplayed()),
  );
  public radioBoxPerIndex: WritableSignal<AllRadioBoxPerIndex> = signal({});
  public downloadPdfButtonConfig: WritableSignal<BlockButton | null> = signal(null);

  protected FormControlName: typeof FormControlName = FormControlName;
  protected ViaSurveySection: typeof ViaSurveySection = ViaSurveySection;

  private radioBoxChangeSubscription: Subscription | null = null;

  @ViewChild('topOfViaSurveyBlock', { read: ElementRef }) public topOfViaSurveyBlock!: ElementRef;
  @ViewChildren('radioBox', { read: ElementRef }) public radioBox!: QueryList<ElementRef>;

  constructor(
    @Optional() @Self() public override controlDir: NgControl,
    private viaSurveyService: ViaSurveyService,
    protected override courseContentService: CourseContentService,
  ) {
    super(controlDir, courseContentService);

    effect(() => {
      this.pageNumber();
      this.radioBoxPerIndex();

      if (this.form) {
        this.formArrayBlocks.clear();
        const indexesQuestionsToDisplay: string[] = this.getQuestionIndexFromPageNumber(this.pageNumber());
        const radioBoxPerIndex: AllRadioBoxPerIndex = this.radioBoxPerIndex();
        for (const index of indexesQuestionsToDisplay) {
          this.formArrayBlocks.push(new FormControl(radioBoxPerIndex[index]) as FormControl<BlockRadioBox>);
        }

        console.log('TONIO BlockViaSurveyComponent  this.formArrayBlocks=', this.formArrayBlocks);

        this.updateValueAndValidityAsync(5);
      }
    });
  }

  protected override initForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<ViaSurveyForm>({
      [FormControlName.viaSurveyQuestions]: fb.nonNullable.array<BlockRadioBox>([], this.validateFormArray.bind(this)),
      [FormControlName.strengthList]: fb.nonNullable.control<CheckboxItem[]>([]),
    });
  }

  public get formArrayBlocks(): FormArray<FormControl<BlockRadioBox>> {
    if (this.form) {
      return this.form.get(FormControlName.viaSurveyQuestions) as FormArray<FormControl<BlockRadioBox>>;
    }

    throw new Error(`this.form is not defined, not normal`);
  }

  protected validateFormArray(abstractControl: AbstractControl): ValidationErrors | null {
    const formArray: FormArray<FormControl<BlockRadioBox>> = abstractControl as FormArray;
    let errors: ValidationErrors | null = null;
    for (const control of formArray.controls) {
      if (control.invalid) {
        if (errors !== null) {
          Object.assign(errors, control.errors);
        } else {
          errors = control.errors;
        }
      }
    }
    return errors;
  }

  protected override patchFormWithEmptyValue(): void {
    if (this.form) {
      this.formArrayBlocks.clear({ emitEvent: false });
    } else {
      throw new Error(`patchFormWithEmptyValue() this.form should not be null when this function is called`);
    }
  }

  protected override patchFormFromBlock(block: BlockViaSurvey, valueFromLocalStorage: string | null): void {
    console.log(
      'TONIO BlockViaSurveyComponent patchFormFromBlock block=',
      block,
      '   this.versionBeforeProgram=',
      this.versionBeforeProgram,
      '   valueFromLocalStorage=',
      valueFromLocalStorage,
    );
    this.showLoaderAndHideSnackbar();
    this.downloadPdfButtonConfig.set(this.getButtonConfigFromBlock(block));

    this.formArrayBlocks.clear({ emitEvent: false });

    if (this.form) {
      this.numberQuestionDisplayed.set(block.data.config.nb_questions_per_page || 20);

      if (block.data.items && block.data.items.length > 0) {
        this.displayResults(block.data.items);
        this.hideLoader();
        if (!this.versionBeforeProgram) {
          this.courseContentService.showSnackbar();
        }
        this.section.set(ViaSurveySection.results);
      } else {
        this.setupViaSurvey().then(() => {
          this.section.set(ViaSurveySection.questions);
          this.hideLoaderAndShowSnackbar();
        });
      }
    } else {
      throw new Error(`patchFormFromBlock() this.form should not be null when this function is called`);
    }
  }

  protected override getUpdatedBlock(): BlockViaSurvey {
    if (this.form && this.block) {
      return {
        ...this.block,
        data: {
          ...this.block.data,
          value: this.formatSelectedListOfStrengthForSavingInViaSurveyBlock(
            this.form.controls[FormControlName.strengthList].value,
          ),
        },
      };
    }

    throw new Error(`getUpdatedBlock() form or block should not be null when this function is called`);
  }

  protected override onProgrammaticFocus(): void {}

  private async setupViaSurvey(): Promise<void> {
    console.log('TONIO BlockViaSurveyComponent setupViaSurvey =');
    await this.viaSurveyService.registerUser(this.destroyRef);
    await this.viaSurveyService.loginUser(this.destroyRef);
    await this.viaSurveyService.startSurvey(this.destroyRef);
    const questions: ViaSurveyQuestion[] = await this.viaSurveyService.getAllViaSurveyQuestions(this.destroyRef);
    const blocksRadioBox: BlockRadioBox[] = this.viaSurveyService.transformQuestionsToRadioBlock(questions);

    const allRadioBoxPerIndex: AllRadioBoxPerIndex = {};
    console.log('TONIO BlockViaSurveyComponent setupViaSurvey blocksRadioBox=', blocksRadioBox);
    blocksRadioBox.forEach((blockRadiobox: BlockRadioBox): void => {
      allRadioBoxPerIndex[blockRadiobox.data.name] = blockRadiobox;
    });
    this.radioBoxPerIndex.set(allRadioBoxPerIndex);

    const pageNumber: number = this.computePageNumberBasedOnFirstEmptyRadiobox(blocksRadioBox);
    this.setPageNumber(pageNumber);
    this.updateValueAndValidityAsync(5);

    setTimeout(() => {
      if (this.radioBoxChangeSubscription) {
        this.radioBoxChangeSubscription.unsubscribe();
      }

      this.radioBoxChangeSubscription = this.formArrayBlocks.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          setTimeout(() => {
            // as we are relying on css class to find the invalid control, we have to wait for the DOM to be updated
            // after it has changed value
            const invalidRadioBox: ElementRef | undefined = this.radioBox.find((radiobox: ElementRef): boolean => {
              return radiobox.nativeElement.classList.contains('ng-invalid');
            });

            if (invalidRadioBox) {
              invalidRadioBox?.nativeElement.scrollIntoView({ behavior: 'smooth' });
            }
          });
        });
    }, 1000);
  }

  private setPageNumber(pageNumber: number): void {
    if (pageNumber === 1) {
      this.courseContentService.addOverrideForButtonLeftAction(null);
      this.courseContentService.addOverrideForButtonRightAction(this.onRightButtonClicked.bind(this));
    } else if (pageNumber > 1 && pageNumber <= this.totalPages()) {
      this.courseContentService.addOverrideForButtonLeftAction(this.onLeftButtonClicked.bind(this));
      this.courseContentService.addOverrideForButtonRightAction(this.onRightButtonClicked.bind(this));
    }
    this.pageNumber.set(pageNumber);

    setTimeout(() => {
      console.log('TONIO BlockViaSurveyComponent setPageNumber this.form=', this.form);
    }, 500);
  }

  private onRightButtonClicked(): void {
    const currentPageNumber: number = this.pageNumber();
    console.log(
      '\x1b[41m TONIO BlockViaSurveyComponent onRightButtonClicked currentPageNumber=',
      currentPageNumber,
      '    this.formArrayBlocks.value=',
      this.formArrayBlocks.value,
      '    this.form=',
      this.form,
      '\x1b',
    );
    this.copyAnswersFromFormArrayAndSaveInLocalStorage();
    this.courseContentService.setMainFormSubmitted(false);
    if (currentPageNumber < this.totalPages()) {
      this.setPageNumber(currentPageNumber + 1);
      if (this.topOfViaSurveyBlock) {
        this.topOfViaSurveyBlock.nativeElement.scrollIntoView();
      }
    } else if (currentPageNumber === this.totalPages()) {
      console.log('\x1b[44m TONIO BlockViaSurveyComponent onRightButtonClicked LAST PAGE =', '\x1b');

      this.submitAnswersAndComputeResults();
    } else {
      throw new Error(
        `currentPageNumber (${currentPageNumber}) should not be more than totalPages (${this.totalPages()})`,
      );
    }
  }

  private onLeftButtonClicked(): void {
    console.log('\x1b[41m TONIO BlockViaSurveyComponent onLeftButtonClicked this=', this, '\x1b');
    this.copyAnswersFromFormArrayAndSaveInLocalStorage();
    this.setPageNumber(this.pageNumber() - 1);

    if (this.topOfViaSurveyBlock) {
      this.topOfViaSurveyBlock.nativeElement.scrollIntoView();
    }
  }

  private getQuestionIndexFromPageNumber(pageNumber: number): string[] {
    const indexArray: string[] = [];
    const indexFirstQuestion: number = (pageNumber - 1) * this.numberQuestionDisplayed();
    for (let i: number = indexFirstQuestion; i < indexFirstQuestion + this.numberQuestionDisplayed(); i++) {
      indexArray.push(`${i}`);
    }

    console.log(
      'TONIO BlockViaSurveyComponent getQuestionIndexFromPageNumber pageNumber=',
      pageNumber,
      '   indexArray=',
      indexArray,
    );
    return indexArray;
  }

  private copyAnswersFromFormArrayAndSaveInLocalStorage(): void {
    this.radioBoxPerIndex.update((allRadioBox: AllRadioBoxPerIndex): AllRadioBoxPerIndex => {
      this.formArrayBlocks.value.forEach((radioBox: BlockRadioBox): void => {
        allRadioBox[radioBox.data.name] = radioBox;
      });

      return allRadioBox;
    });

    const shortCodesToSave: ShortCodes = {};
    const validRadioBlocks: BlockRadioBox[] = this.formArrayBlocks.value.filter(
      (radioBlock: BlockRadioBox) => !!radioBlock.data.value,
    );
    console.log(
      'TONIO BlockViaSurveyComponent copyAnswersFromFormArrayAndSaveInLocalStorage validRadioBlocks=',
      validRadioBlocks,
    );
    validRadioBlocks.forEach((radioBlock: BlockRadioBox): void => {
      shortCodesToSave[radioBlock.program_data_code] = radioBlock.data.value;
    });
    this.courseContentService.saveShortCodesInLocalStorage(shortCodesToSave);
  }

  private computePageNumberBasedOnFirstEmptyRadiobox(questions: BlockRadioBox[]): number {
    for (const [index, question] of questions.entries()) {
      const hasQuestionBeenAnswered: boolean = !!question.data.items.find((item: RadioItem) => item.selected);

      if (!hasQuestionBeenAnswered) {
        console.log(
          'TONIO BlockViaSurveyComponent computePageNumberBasedOnFirstEmptyRadiobox first empty block at index=',
          index,
        );
        return Math.floor(index / this.numberQuestionDisplayed()) + 1;
      }
    }

    return this.totalPages();
  }

  private async submitAnswersAndComputeResults(): Promise<void> {
    console.log(
      'TONIO 1 BlockViaSurveyComponent submitAnswersAndComputeResults dirty=',
      this.form?.controls[FormControlName.strengthList].dirty,
    );

    this.showLoaderAndHideSnackbar();
    console.log(
      'TONIO 1.5 BlockViaSurveyComponent submitAnswersAndComputeResults dirty=',
      this.form?.controls[FormControlName.strengthList].dirty,
    );

    await this.viaSurveyService.submitAnswers(Object.values(this.radioBoxPerIndex()), this.destroyRef);
    console.log(
      'TONIO 1.8 BlockViaSurveyComponent submitAnswersAndComputeResults dirty=',
      this.form?.controls[FormControlName.strengthList].dirty,
    );

    const results: ViaSurveyStrength[] = await this.viaSurveyService.getResults(this.destroyRef);
    console.log(
      'TONIO 2 BlockViaSurveyComponent submitAnswersAndComputeResults dirty=',
      this.form?.controls[FormControlName.strengthList].dirty,
    );

    this.block = this.updateViaSurveyBlockWithStrengthList(this.block as BlockViaSurvey, results);
    console.log(
      'TONIO BlockViaSurveyComponent submitAnswersAndComputeResults this.versionBeforeProgram=',
      this.versionBeforeProgram,
    );
    await this.courseContentService.saveProgramDataCodesAndUpdateStep(
      this.versionBeforeProgram ? ApiUrlProgramData.surveyNotLoggedIn : ApiUrlProgramData.loggedIn,
      this.getProgramDataCodesToSave(results),
      this.block,
    );
    console.log(
      'TONIO 3 BlockViaSurveyComponent submitAnswersAndComputeResults dirty=',
      this.form?.controls[FormControlName.strengthList].dirty,
    );

    console.log('TONIO BlockViaSurveyComponent submitAnswersAndComputeResults results=', results);
    this.displayResults(results);
    this.section.set(ViaSurveySection.results);
    this.hideLoader();
    if (!this.versionBeforeProgram) {
      this.courseContentService.showSnackbar();
    }
  }

  private showLoaderAndHideSnackbar(): void {
    this.isLoading.set(true);
    this.courseContentService.hideSnackbar();
  }

  private hideLoaderAndShowSnackbar(): void {
    this.hideLoader();
    this.courseContentService.showSnackbar();
  }

  private hideLoader(): void {
    this.isLoading.set(false);
  }

  private getProgramDataCodesToSave(viaSurveyResults: ViaSurveyStrength[]): ProgramDataCode[] {
    if (!this.block?.data.config.all_strengths) {
      throw new Error(`Missing shortcode for all_strength in block config`);
    }

    const programDataCodesToSave: ProgramDataCode[] = [
      {
        code: this.block?.data.config.all_strengths,
        value: viaSurveyResults,
      },
    ];

    if (this.versionBeforeProgram) {
      programDataCodesToSave.push({
        code: TOKEN_SURVEY_QUERY_PARAM_KEY,
        value: this.tokenSurvey,
      });
    }

    return programDataCodesToSave;
  }

  private updateViaSurveyBlockWithStrengthList(
    viaSurveyBlock: BlockViaSurvey,
    viaSurveyStrengths: ViaSurveyStrength[],
  ): BlockViaSurvey {
    return {
      ...viaSurveyBlock,
      data: {
        ...viaSurveyBlock.data,
        items: viaSurveyStrengths,
      },
    };
  }

  private displayResults(strengthList: ViaSurveyStrength[]): void {
    console.log('TONIO BlockViaSurveyComponent displayResults strengthList=', strengthList);
    if (this.versionBeforeProgram) {
      this.courseContentService.hideSnackbar();
    } else {
      const selectedStrength: string[] = this.block?.data.value ? JSON.parse(this.block?.data.value) : [];
      console.log(
        'TONIO 1 BlockViaSurveyComponent displayResults dirty=',
        this.form?.controls[FormControlName.strengthList].dirty,
      );
      const checkboxItemsOfViaSurveyStrengthFromParticipant: CheckboxItem[] =
        this.viaSurveyService.transformStrengthListToCheckboxItems(strengthList, selectedStrength);
      this.form?.controls[FormControlName.strengthList].setValue(checkboxItemsOfViaSurveyStrengthFromParticipant);
      console.log(
        'TONIO 2 BlockViaSurveyComponent displayResults dirty=',
        this.form?.controls[FormControlName.strengthList].dirty,
      );

      this.courseContentService.addOverrideForButtonLeftAction(null);
      this.courseContentService.addOverrideForButtonRightAction(null);
    }

    this.viaSurveyService.addViaSurveyStrengthShortCodeForPDFGeneration(strengthList);
  }

  private formatSelectedListOfStrengthForSavingInViaSurveyBlock(checkboxItems: CheckboxItem[]): string {
    const selectedStrength: CheckboxItem[] = checkboxItems.filter((checkboxItem: CheckboxItem) => checkboxItem.checked);
    return JSON.stringify(selectedStrength.map((strength: CheckboxItem): string => strength.value));
  }

  public getButtonConfigFromBlock(viaSurveyBlock: BlockViaSurvey): BlockButton {
    return {
      id: getRandomInt(),
      element: BlockType.button,
      source_program_data_code: null,
      program_data_code: null,
      data: {
        name: getRandomId(),
        type: ButtonType.pdf,
        color: ButtonColor.primary,
        href: viaSurveyBlock.data.config.before_program_button_pdf.href,
        label: viaSurveyBlock.data.config.before_program_button_pdf.label,
        position: ButtonPosition.center,
      },
    };
  }
}
